import { useCallback, useEffect, useRef, useState } from 'react';

const clampVolume = (value) => Math.max(0, Math.min(1, Number(value) || 0));
const toPositiveMs = (value, fallback) => {
  const next = Number(value);
  return Number.isFinite(next) && next > 0 ? next : fallback;
};
const toSeconds = (value, fallback) => {
  const next = Number(value);
  return Number.isFinite(next) && next >= 0 ? next : fallback;
};

const getAudioCtor = () => {
  if (typeof window !== 'undefined' && typeof window.Audio === 'function') {
    return window.Audio;
  }
  if (typeof Audio === 'function') return Audio;
  return null;
};

const getTimerRoot = () => {
  if (typeof window !== 'undefined') return window;
  return {
    setTimeout,
    clearTimeout,
  };
};

const getFrameApi = () => {
  const timerRoot = getTimerRoot();
  if (
    typeof window !== 'undefined' &&
    typeof window.requestAnimationFrame === 'function' &&
    typeof window.cancelAnimationFrame === 'function'
  ) {
    return {
      request: window.requestAnimationFrame.bind(window),
      cancel: window.cancelAnimationFrame.bind(window),
    };
  }

  return {
    request: (callback) => timerRoot.setTimeout(() => callback(Date.now()), 16),
    cancel: (frameId) => timerRoot.clearTimeout(frameId),
  };
};

export default function usePolishedAudioSting({
  url,
  enabled = true,
  clipStartSeconds = 0,
  clipEndSeconds = null,
  targetVolume = 0.42,
  fadeInMs = 320,
  fadeOutMs = 800,
} = {}) {
  const audioRef = useRef(null);
  const frameRef = useRef(null);
  const fadeOutTimerRef = useRef(null);
  const mountedRef = useRef(false);
  const playingRef = useRef(false);
  const blockedRef = useRef(false);

  const [blocked, setBlocked] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [playedOnce, setPlayedOnce] = useState(false);

  const clipStart = toSeconds(clipStartSeconds, 0);
  const clipEnd = clipEndSeconds === null ? null : toSeconds(clipEndSeconds, null);
  const fadeInDuration = toPositiveMs(fadeInMs, 320);
  const fadeOutDuration = toPositiveMs(fadeOutMs, 800);
  const resolvedTargetVolume = clampVolume(targetVolume);
  const clipDurationMs =
    clipEnd !== null && clipEnd > clipStart ? (clipEnd - clipStart) * 1000 : null;

  const safeSetBlocked = useCallback((value) => {
    blockedRef.current = value;
    if (mountedRef.current) setBlocked(value);
  }, []);

  const safeSetPlaying = useCallback((value) => {
    playingRef.current = value;
    if (mountedRef.current) setPlaying(value);
  }, []);

  const safeSetPlayedOnce = useCallback((value) => {
    if (mountedRef.current) setPlayedOnce(value);
  }, []);

  const cancelRamp = useCallback(() => {
    if (frameRef.current !== null) {
      getFrameApi().cancel(frameRef.current);
      frameRef.current = null;
    }
  }, []);

  const clearFadeOutTimer = useCallback(() => {
    if (fadeOutTimerRef.current !== null) {
      const timerRoot = getTimerRoot();
      timerRoot.clearTimeout(fadeOutTimerRef.current);
      fadeOutTimerRef.current = null;
    }
  }, []);

  const resetAudio = useCallback(() => {
    cancelRamp();
    clearFadeOutTimer();
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.pause();
      audio.volume = 0;
      audio.currentTime = clipStart;
    } catch (error) {
      // Browser media state can reject seeks; audio is optional enhancement.
    }
    audioRef.current = null;
    safeSetPlaying(false);
  }, [cancelRamp, clearFadeOutTimer, clipStart, safeSetPlaying]);

  const fadeVolume = useCallback(
    (toVolume, durationMs, onDone) => {
      const audio = audioRef.current;
      if (!audio) {
        if (typeof onDone === 'function') onDone();
        return;
      }

      cancelRamp();
      const api = getFrameApi();
      const fromVolume = clampVolume(audio.volume);
      const target = clampVolume(toVolume);
      let startedAt = null;

      const step = (timestamp) => {
        const frameTime = Number.isFinite(Number(timestamp)) ? Number(timestamp) : Date.now();
        if (startedAt === null) startedAt = frameTime;
        const elapsed = frameTime - startedAt;
        const progress = durationMs <= 0 ? 1 : Math.min(1, elapsed / durationMs);
        const nextVolume = fromVolume + (target - fromVolume) * progress;
        try {
          audio.volume = clampVolume(nextVolume);
        } catch (error) {
          // Volume ramp failure should never break completion UI.
        }

        if (progress >= 1) {
          frameRef.current = null;
          if (typeof onDone === 'function') onDone();
          return;
        }

        frameRef.current = api.request(step);
      };

      frameRef.current = api.request(step);
    },
    [cancelRamp],
  );

  const stop = useCallback(() => {
    const audio = audioRef.current;
    clearFadeOutTimer();

    if (!audio || !playingRef.current) {
      resetAudio();
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      fadeVolume(0, fadeOutDuration, () => {
        resetAudio();
        resolve();
      });
    });
  }, [clearFadeOutTimer, fadeOutDuration, fadeVolume, resetAudio]);

  const play = useCallback(async () => {
    if (!enabled || !url || playingRef.current) return false;

    const AudioCtor = getAudioCtor();
    if (!AudioCtor) {
      safeSetBlocked(true);
      return false;
    }

    let audio = audioRef.current;
    if (!audio) {
      audio = new AudioCtor(url);
      audioRef.current = audio;
      audio.crossOrigin = 'anonymous';
      audio.preload = 'metadata';
    }

    cancelRamp();
    clearFadeOutTimer();
    safeSetBlocked(false);

    try {
      audio.pause();
      audio.volume = 0;
      audio.currentTime = clipStart;
    } catch (error) {
      // Optional audio enhancement; keep UI alive even if the seek fails.
    }

    try {
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.then === 'function') {
        await playPromise;
      }
    } catch (error) {
      safeSetBlocked(true);
      resetAudio();
      return false;
    }

    safeSetPlaying(true);
    safeSetPlayedOnce(true);
    fadeVolume(resolvedTargetVolume, fadeInDuration);

    if (clipDurationMs !== null) {
      const timerRoot = getTimerRoot();
      const fadeOutDelay = Math.max(0, clipDurationMs - fadeOutDuration);
      fadeOutTimerRef.current = timerRoot.setTimeout(() => {
        fadeOutTimerRef.current = null;
        stop();
      }, fadeOutDelay);
    }

    return true;
  }, [
    cancelRamp,
    clearFadeOutTimer,
    clipDurationMs,
    clipStart,
    enabled,
    fadeInDuration,
    fadeOutDuration,
    fadeVolume,
    resetAudio,
    resolvedTargetVolume,
    safeSetBlocked,
    safeSetPlayedOnce,
    safeSetPlaying,
    stop,
    url,
  ]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      resetAudio();
    };
  }, [resetAudio]);

  useEffect(() => {
    if (!enabled) resetAudio();
  }, [enabled, resetAudio]);

  return {
    play,
    stop,
    blocked,
    playing,
    playedOnce,
  };
}
